import React, { useEffect, useState } from 'react';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import { UNITED_STATES_ID } from 'utils/constants/Country';
import { StringConstantsMixin, ProPageStrings } from 'react_components/mixins/shared/string_constants_mixin';
import SectionDivider from 'components/shared/SectionDivider';
import Button from 'components/shared/Button';
import { loadRecaptcha, executeRecaptcha } from 'utils/recaptcha_helpers';
import { validateUserForm } from 'utils/form_validaters/user_form_helper';
import { facebookHandler, appleHandler, googleHandler } from 'utils/OAuthHandlers';
import { ServerCommunicationUtil } from 'utils/server_communication_util';
import { LanguageSupportUtil } from 'utils/language_support_util';
import { OptimizelyConstants } from 'utils/OptimizelyHelpers';
import { logSignUpSuccess } from '@alltrails/analytics/amplitudeFunctions';
import { AuthenticationType } from '@alltrails/analytics/amplitudeEnums';
import OutlinedTextField from '../textFields/OutlinedTextField';
import Checkbox from '../Checkbox';
import AltLoginsForm from '../AltLoginsForm';
import * as baseStyles from '../../../styles/baseStyles.module.scss';
import * as formStyles from '../styles/styles.module.scss';
import * as styles from './styles/styles.module.scss';
import logError from '../../../utils/logError';

const {
  formText: { login, loginLead }
} = ProPageStrings;

const {
  OR,
  SIGNUP: { SIGNUP }
} = StringConstantsMixin.strings;

function trackRegistrationInTatari() {
  // Safety first - Wrap anything Tatari-related in a try/catch
  try {
    if (window.tatari) {
      window.tatari.track('signup');
    }
  } catch (error) {
    logError(error);
  }
}

const REGISTRATION_URL = '/api/alltrails/register';

type Props = {
  countryId: number;
  enableRecaptcha: boolean;
  facebookAppId: number;
  handleOptimizelyEvent: (eventType: string, eventOutcome: string, eventAttributes?: any) => void;
  handleRegistrationSuccess: Function;
  redirectTo: string;
  text: {
    email: {
      title: string;
    };
    firstName: {
      title: string;
    };
    lastName: {
      title: string;
    };
    password: {
      title: string;
    };
  };
  userEmailSubscribed?: boolean;
  handleLoginClick: React.MouseEventHandler<HTMLAnchorElement>;
  referralLink: string;
};

const RegistrationForm: React.FC<Props> = ({
  countryId,
  enableRecaptcha,
  facebookAppId,
  handleOptimizelyEvent,
  handleRegistrationSuccess,
  redirectTo,
  text,
  userEmailSubscribed,
  handleLoginClick,
  referralLink
}) => {
  const [state, setState] = useState<{
    serverError: string;
    userEmailSubscribed: boolean;
    userRegLastName: string;
    userRegEmail: string;
    userRegPassword: string;
    userRegFirstName: string;
    submitting: boolean;
    disableSubmit: boolean;
    recaptchaObject: any;
    showErrorsOnType: boolean;
  }>({
    userRegEmail: '',
    userRegPassword: '',
    userRegFirstName: '',
    userRegLastName: '',
    userEmailSubscribed,
    serverError: null,
    submitting: false,
    disableSubmit: enableRecaptcha,
    recaptchaObject: null,
    showErrorsOnType: false
  });

  const [errors, setErrors] = useState<{ userRegEmail?: string; userRegPassword?: string; userRegFirstName?: string; userRegLastName?: string }>({});

  const locale = useLanguageRegionCode();

  useEffect(() => {
    if (enableRecaptcha) {
      loadRecaptcha((recaptchaObject: any) => {
        setState({ ...state, disableSubmit: false, recaptchaObject });
      });
    }
  }, []);

  function showEmailSubscribed() {
    return countryId !== UNITED_STATES_ID;
  }

  function handleSubscribedChange(e: React.ChangeEvent<HTMLInputElement>) {
    e.preventDefault();
    if (!showEmailSubscribed()) {
      return;
    }

    const { userEmailSubscribed } = state;
    setState({ ...state, userEmailSubscribed: !userEmailSubscribed });
  }

  function registrationSuccessCallback(data: any, signupMethod: AuthenticationType) {
    if (handleOptimizelyEvent) {
      handleOptimizelyEvent(OptimizelyConstants.EVENTS.SIGNUP_SUCCESS, OptimizelyConstants.OUTCOMES.SUCCESS, { signupMethod });
    }
    if (handleRegistrationSuccess) {
      handleRegistrationSuccess(data);
    }

    trackRegistrationInTatari();

    setErrors({});

    setState({
      userRegEmail: '',
      userRegPassword: '',
      userRegFirstName: '',
      userRegLastName: '',
      userEmailSubscribed,
      serverError: null,
      submitting: false,
      disableSubmit: enableRecaptcha,
      recaptchaObject: null,
      showErrorsOnType: false
    });

    logSignUpSuccess({ authentication_type: signupMethod });

    if (!redirectTo) {
      return;
    }

    window.location.href = redirectTo;
  }

  function handleGoogle(response: any) {
    googleHandler(
      response,
      locale,
      (data: any) => registrationSuccessCallback(data, AuthenticationType.Google),
      () => {},
      () => null,
      referralLink
    );
  }

  function handleFacebook(response: any) {
    facebookHandler(
      response,
      locale,
      (data: any) => registrationSuccessCallback(data, AuthenticationType.Facebook),
      () => {},
      () => null,
      referralLink
    );
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    e.preventDefault();
    const { name, value } = e.target;
    // we don't want to show errors on type unless a user has already tried to submit a form
    if (state.showErrorsOnType) {
      setErrors(validateUserForm(name, value, errors).errors);
    }

    setState({ ...state, [name]: value });
  }

  function handleErrorCallback(err: { responseJSON: { errors: any[] } }) {
    let { serverError } = state;
    const serverResponseError = err && err.responseJSON ? err.responseJSON.errors[0] : { message: StringConstantsMixin.strings.ERR_TRY_AGAIN };
    if (serverResponseError && serverResponseError.target === 'email') {
      setErrors({ ...errors, userRegEmail: serverResponseError.message });
      serverError = serverResponseError.message;
    } else if (serverResponseError && serverResponseError.message) {
      serverError = serverResponseError.message;
    }
    setState({ ...state, serverError, submitting: false });
    if (handleOptimizelyEvent) {
      handleOptimizelyEvent(OptimizelyConstants.EVENTS.SIGNUP_CLICKED, OptimizelyConstants.OUTCOMES.FAILED_VALIDATION);
    }
  }

  function submitRegister(recaptchaToken?: string) {
    // API params
    // required =>  :first_name, :last_name, :email, :password
    // optional => :create_session, :subscribed, :recaptcha
    const headers = locale ? { 'X-Language-Locale': locale } : {};
    const params = {
      first_name: state.userRegFirstName,
      last_name: state.userRegLastName,
      email: state.userRegEmail,
      password: state.userRegPassword,
      create_session: true,
      subscribed: state.userEmailSubscribed,
      recaptcha: recaptchaToken,
      referral_code: referralLink
    };

    ServerCommunicationUtil.postApiEndpoint(
      REGISTRATION_URL,
      params,
      (data: any) => registrationSuccessCallback(data, AuthenticationType.Email),
      handleErrorCallback,
      (): void => null,
      headers
    );
  }

  function handleRegister(e: React.KeyboardEvent<HTMLDivElement> | React.FormEvent) {
    e.preventDefault();
    if (state.disableSubmit || state.submitting) {
      return;
    }

    let validationErrors = {};
    validationErrors = validateUserForm('userRegFirstName', state.userRegFirstName, validationErrors).errors;
    validationErrors = validateUserForm('userRegLastName', state.userRegLastName, validationErrors).errors;
    validationErrors = validateUserForm('userRegEmail', state.userRegEmail, validationErrors).errors;
    validationErrors = validateUserForm('userRegPassword', state.userRegPassword, validationErrors).errors;

    if (Object.keys(validationErrors).length !== 0) {
      setErrors(validationErrors);
      setState({ ...state, showErrorsOnType: true });

      if (handleOptimizelyEvent) {
        handleOptimizelyEvent(OptimizelyConstants.EVENTS.SIGNUP_CLICKED, OptimizelyConstants.OUTCOMES.FAILED_VALIDATION);
      }

      return;
    }

    setState({ ...state, submitting: true });
    // Need to get recaptchaToken here/now, because a token can only be used once,
    // and server may reject form details requiring the user to edit and resubmit.
    if (enableRecaptcha) {
      executeRecaptcha(state.recaptchaObject, 'userSignupLogin').then(
        (recaptchaToken: any) => submitRegister(recaptchaToken),
        () => setState({ ...state, submitting: false })
      );
    } else {
      submitRegister();
    }
  }

  function renderEmailTextField() {
    return (
      <div className={`${formStyles.inputItem}`} data-testid="registrationForm-emailTextFeild">
        <OutlinedTextField
          name="userRegEmail"
          errorMessage={errors.userRegEmail}
          value={state.userRegEmail}
          label={text.email.title}
          changeHandler={e => handleChange(e)}
          enterKeyHandler={handleRegister}
          type="email"
        />
      </div>
    );
  }

  function renderPasswordTextField() {
    return (
      <div
        className={`${formStyles.inputItem} ${showEmailSubscribed() ? styles.showSubscribedDistance : ''} `}
        data-testid="registrationForm-passwordTextFeild"
      >
        <OutlinedTextField
          name="userRegPassword"
          errorMessage={errors.userRegPassword}
          value={state.userRegPassword}
          label={text.password.title}
          changeHandler={e => handleChange(e)}
          enterKeyHandler={handleRegister}
          type="password"
        />
      </div>
    );
  }

  function renderEmailOptinCheckbox() {
    if (!showEmailSubscribed()) {
      return null;
    }

    return (
      <Checkbox
        handleChange={handleSubscribedChange}
        checked={state.userEmailSubscribed}
        label={StringConstantsMixin.strings.SIGNUP.EMAIL_SUBSCRIBE}
      />
    );
  }

  function renderServerError() {
    if (!state.serverError) {
      return null;
    }

    return <div className={`${formStyles.serverError} ${styles.center}`}>{state.serverError}</div>;
  }

  function renderFormFields() {
    const disabled = state.disableSubmit || state.submitting;

    return (
      <form id="signup-form" onSubmit={handleRegister}>
        <div className={`${formStyles.inputItem}`} data-testid="registrationForm-firstNameTextField">
          <OutlinedTextField
            name="userRegFirstName"
            errorMessage={errors.userRegFirstName}
            value={state.userRegFirstName}
            label={text.firstName.title}
            changeHandler={e => handleChange(e)}
            enterKeyHandler={handleRegister}
          />
        </div>
        <div className={`${formStyles.inputItem}`} data-testid="registrationForm-lastNameTextField">
          <OutlinedTextField
            name="userRegLastName"
            errorMessage={errors.userRegLastName}
            value={state.userRegLastName}
            label={text.lastName.title}
            changeHandler={e => handleChange(e)}
            enterKeyHandler={handleRegister}
          />
        </div>
        {renderEmailTextField()}
        {renderPasswordTextField()}
        {renderEmailOptinCheckbox()}
        {renderServerError()}
        <Button classes={styles.signup} disabled={disabled} type="submit" variant="primary">
          {SIGNUP}
        </Button>
      </form>
    );
  }

  return (
    <div>
      <AltLoginsForm
        facebookAppId={facebookAppId}
        googleLoginHandler={handleGoogle}
        facebookLoginHandler={handleFacebook}
        appleLoginHandler={() => appleHandler(referralLink)}
      />
      <SectionDivider classes={styles.sectionDivider}>{OR}</SectionDivider>
      <div className={baseStyles.section}>{renderFormFields()}</div>
      <div className={styles.alreadyHaveAnAccount}>
        {loginLead}{' '}
        <a className={baseStyles.route} href={LanguageSupportUtil.wrapUrlSafe('/login', locale)} onClick={handleLoginClick}>
          {login}
        </a>
      </div>
    </div>
  );
};

export default RegistrationForm;
